// ============================================================
//  AUTH STORE — Firebase Auth backed
// ============================================================

import { create }                              from 'zustand';
import { ref, get as fbGet, set as fbSet,
         push as fbPush }                      from 'firebase/database';
import { onAuthStateChanged, auth,
         signInAdmin, signInCrew,
         firebaseSignOut }                     from '../firebase/config';
import type { User }                           from '../firebase/config';
import { db }                                  from '../firebase/config';
import type { AuthRole }                       from '../types';

const SESSION_KEY = 'fleetops_session';

interface AuthState {
  role:         AuthRole;
  name:         string | null;
  firebaseUser: User | null;
  ready:        boolean;
  authError:    string | null;

  loginAdmin:  (email: string, password: string) => Promise<boolean>;
  loginCrew:   (name: string) => void;
  logout:      () => Promise<void>;
  boot:        () => void;
  clearError:  () => void;

  isAdmin: () => boolean;
  isCrew:  () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  role:         null,
  name:         null,
  firebaseUser: null,
  ready:        false,
  authError:    null,

  isAdmin:    () => get().role === 'admin',
  isCrew:     () => get().role === 'crew',
  clearError: () => set({ authError: null }),

  // ── Admin login — Firebase email + password
  // Supports multiple admins via admin_uids node (matches your database structure)
  async loginAdmin(email, password) {
    set({ authError: null });
    try {
      const user = await signInAdmin(email, password);

      // Check admin_uids node — supports multiple admins
      const snap      = await fbGet(ref(db, 'admin_uids'));
      const adminUids = snap.val() as Record<string, string> | null;

      if (adminUids) {
        // Admin UIDs exist — check if this user is one of them
        const isRegistered = Object.values(adminUids).includes(user.uid);
        if (!isRegistered) {
          await firebaseSignOut();
          set({ authError: 'Not authorised as admin.' });
          return false;
        }
      } else {
        // No admins registered yet — register this as the first admin
        await fbPush(ref(db, 'admin_uids'), user.uid);
      }

      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ role: 'admin', name: null }));
      set({ role: 'admin', name: null, firebaseUser: user, ready: true });
      return true;

    } catch (e: any) {
      const code = e?.code as string | undefined;
      const msg  =
        code === 'auth/invalid-credential' || code === 'auth/wrong-password'
          ? 'Incorrect email or password.'
          : code === 'auth/too-many-requests'
          ? 'Too many attempts — try again later.'
          : code === 'auth/invalid-email'
          ? 'Invalid email address.'
          : 'Login failed. Check your credentials.';
      set({ authError: msg });
      return false;
    }
  },

  // ── Crew login — wait for anonymous auth BEFORE setting role
  // This ensures Firebase auth token is ready before data sync starts
  loginCrew(name) {
    signInCrew()
      .then(user => {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ role: 'crew', name }));
        // Set firebaseUser AND role together — sync won't start until both are set
        set({ role: 'crew', name, firebaseUser: user, ready: true });
      })
      .catch(() => {
        // Anonymous auth failed — still let them in but without Firebase sync
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ role: 'crew', name }));
        set({ role: 'crew', name, ready: true });
      });
  },

  // ── Logout
  async logout() {
    sessionStorage.removeItem(SESSION_KEY);
    await firebaseSignOut().catch(() => {});
    set({ role: null, name: null, firebaseUser: null, ready: false });
    window.location.reload();
  },

  // ── Boot: restore session + wire Firebase Auth state listener
  boot() {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        const s = JSON.parse(saved) as { role: AuthRole; name: string | null };
        // Restore role/name from session but do NOT set ready yet
        // Wait for onAuthStateChanged to confirm Firebase auth
        set({ role: s.role, name: s.name || null });
      } catch {}
    }

    onAuthStateChanged(auth, user => {
      if (user) {
        // Firebase auth confirmed — now safe to start sync
        set({ firebaseUser: user, ready: true });
      } else {
        const current = get().role;
        if (current === 'crew') {
          // Crew needs anonymous auth — sign in silently
          signInCrew()
            .then(u => set({ firebaseUser: u, ready: true }))
            .catch(()  => set({ ready: true }));
        } else if (current === 'admin') {
          // Admin session expired — force re-login
          sessionStorage.removeItem(SESSION_KEY);
          set({ role: null, name: null, ready: true });
        } else {
          set({ ready: true });
        }
      }
    });
  },
}));